import { supabase } from './supabase';

export type PlanSlug = 'basic' | 'pro' | 'business';
export type BillingInterval = 'month' | 'year';

export interface PlanConfig {
    id: PlanSlug;
    name: string;
    description: string;
    monthlyPrice: string;
    annualMonthlyPrice: string;
    annualTotalPrice: string;
    priceIdMonth: string;
    priceIdYear: string;
}

export const STRIPE_PLANS: Record<PlanSlug, PlanConfig> = {
    basic: {
        id: 'basic',
        name: 'Basic',
        description: 'Para profissionais autônomos e pequenos projetos que precisam de validações pontuais.',
        monthlyPrice: '29,90',
        annualMonthlyPrice: '24,91',
        annualTotalPrice: '299,00',
        priceIdMonth: 'price_1U5AGNBrxpKuyvXsQwVz1nQX',
        priceIdYear: 'price_1U5AJ7BrxpKuyvXsK7SsVczX',
    },
    pro: {
        id: 'pro',
        name: 'Pro',
        description: 'O mais escolhido por estrategistas, copywriters e equipes de produto para validações contínuas.',
        monthlyPrice: '79,90',
        annualMonthlyPrice: '66,58',
        annualTotalPrice: '799,00',
        priceIdMonth: 'price_1U5AQSBrxpKuyvXsVlRhUN6S',
        priceIdYear: 'price_1U5AQSBrxpKuyvXs8XjYQMBb',
    },
    business: {
        id: 'business',
        name: 'Business',
        description: 'Para agências, consultorias e empresas que desejam múltiplos acessos e atendimento dedicado.',
        monthlyPrice: '249,90',
        annualMonthlyPrice: '183,33',
        annualTotalPrice: '2.200,00',
        priceIdMonth: 'price_1U5AWrBrxpKuyvXsFztZzm3L',
        priceIdYear: 'price_1U5AWrBrxpKuyvXs3PMVNeBs',
    },
};

const ALLOWED_PLANS: ReadonlySet<string> = new Set<PlanSlug>(['basic', 'pro', 'business']);
const ALLOWED_INTERVALS: ReadonlySet<string> = new Set<BillingInterval>(['month', 'year']);

export interface ResolvedPlanSelection {
    plan: PlanConfig;
    interval: BillingInterval;
    priceId: string;
}

/**
 * Sanitiza e valida parâmetros vindos da URL (ex: ?plan=pro&billing=year ou ?plan=basic).
 * Retorna os dados do plano e o priceId correspondente, ou null se o plano for inválido.
 */
export function validateAndResolvePlan(
    rawPlan?: string | null,
    rawInterval?: string | null
): ResolvedPlanSelection | null {
    if (!rawPlan || typeof rawPlan !== 'string') {
        return null;
    }

    const cleanPlan = rawPlan.toLowerCase().trim();
    if (!ALLOWED_PLANS.has(cleanPlan)) {
        return null;
    }

    const plan = STRIPE_PLANS[cleanPlan as PlanSlug];
    if (!plan) {
        return null;
    }

    let interval: BillingInterval = 'year'; // Default mais vantajoso (anual)
    if (rawInterval && typeof rawInterval === 'string') {
        const cleanInterval = rawInterval.toLowerCase().trim();
        if (ALLOWED_INTERVALS.has(cleanInterval)) {
            interval = cleanInterval as BillingInterval;
        } else if (cleanInterval === 'anual' || cleanInterval === 'annual') {
            interval = 'year';
        } else if (cleanInterval === 'mensal' || cleanInterval === 'monthly') {
            interval = 'month';
        }
    }

    const priceId = interval === 'year' ? plan.priceIdYear : plan.priceIdMonth;

    return {
        plan,
        interval,
        priceId,
    };
}

/**
 * Invoca a Edge Function create-checkout-session e retorna a URL de checkout da Stripe.
 */
export async function createStripeCheckoutSession(priceId: string): Promise<string> {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.chatpersonalab.com.br';

    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
            priceId,
            successUrl: `${origin}/configuracoes?status=success`,
            cancelUrl: `${origin}/dashboard?status=canceled`,
        },
    });

    if (error) {
        throw new Error(error.message || 'Falha ao conectar com o serviço de pagamentos.');
    }

    if (!data?.url) {
        throw new Error('URL de checkout não retornada pelo servidor.');
    }

    return data.url as string;
}
